/** Roteador de hash, escrito do zero (sem dependencias). */

export interface RouteContext {
  params: Record<string, string>;
  query: URLSearchParams;
  path: string;
  navigate: (path: string) => void;
}

export type RouteHandler = (context: RouteContext) => void | Promise<void>;

interface RouteDefinition {
  segments: string[];
  handler: RouteHandler;
}

function parseHash(): { path: string; query: URLSearchParams } {
  const raw = window.location.hash.replace(/^#/, '') || '/';
  const hashQueryIndex = raw.indexOf('?');
  const pathPart = hashQueryIndex >= 0 ? raw.slice(0, hashQueryIndex) : raw;
  const queryString = hashQueryIndex >= 0 ? raw.slice(hashQueryIndex + 1) : '';
  return {
    path: pathPart.startsWith('/') ? pathPart : `/${pathPart}`,
    query: new URLSearchParams(queryString),
  };
}

export class Router {
  private readonly routes: RouteDefinition[] = [];
  private fallback: RouteHandler | null = null;
  private notFound: RouteHandler | null = null;

  add(pattern: string, handler: RouteHandler): this {
    this.routes.push({
      segments: pattern.split('/').filter(Boolean),
      handler,
    });
    return this;
  }

  setFallback(handler: RouteHandler): this {
    this.fallback = handler;
    return this;
  }

  setNotFound(handler: RouteHandler): this {
    this.notFound = handler;
    return this;
  }

  navigate(path: string, options: { replace?: boolean } = {}): void {
    const target = path.startsWith('#') ? path : `#${path}`;
    if (window.location.hash === target) {
      void this.resolve();
      return;
    }
    if (options.replace) {
      window.history.replaceState(null, '', target);
      void this.resolve();
    } else {
      window.location.hash = target;
    }
  }

  start(): void {
    window.addEventListener('hashchange', () => void this.resolve());
    void this.resolve();
  }

  private match(segments: string[]): { handler: RouteHandler; params: Record<string, string> } | null {
    for (const route of this.routes) {
      if (route.segments.length !== segments.length) continue;

      const params: Record<string, string> = {};
      let matched = true;

      for (let index = 0; index < route.segments.length; index += 1) {
        const pattern = route.segments[index] as string;
        const value = segments[index] as string;

        if (pattern.startsWith(':')) {
          params[pattern.slice(1)] = decodeURIComponent(value);
        } else if (pattern !== value) {
          matched = false;
          break;
        }
      }

      if (matched) return { handler: route.handler, params };
    }

    return null;
  }

  async resolve(): Promise<void> {
    const { path, query } = parseHash();
    const segments = path.split('/').filter(Boolean);
    const navigate = (target: string): void => this.navigate(target);

    // A rota raiz ("/") tambem gera zero segmentos, por isso a busca por
    // correspondencia vem antes do fallback.
    const found = this.match(segments);
    if (found) {
      await found.handler({ params: found.params, query, path, navigate });
      return;
    }

    if (this.fallback) {
      await this.fallback({ params: {}, query, path, navigate });
      return;
    }

    if (this.notFound) await this.notFound({ params: {}, query, path, navigate });
  }
}
